import React, { FC } from 'react'
import Navbar from 'components/Navbar'
import styled from 'styled-components'
import { ExternalLink } from 'components/ExternalLink'
import PagesDropdown from '../PagesDropdown'
import Breadcrumb from 'components/Breadcrumb'
// Prevent circular dependencies
import { useVersioning } from 'modules/versioning/ShareVersionContext/useVersioning'

const BreadcrumbWrapper = styled.div`
  overflow: hidden;
  margin: 0;
`

const Title = styled(Navbar.Title)`
  padding-right: 0;
`

type EmbeddedBreadcrumbProps = {
  documentName: string
}

/**
 * Helper component for rendering the breadcrumb area of the embedded header.
 */
const EmbeddedBreadcrumb: FC<EmbeddedBreadcrumbProps> = props => {
  const { documentName } = props
  const { share, ...context } = useVersioning()
  const currentVersion = context.loading ? undefined : context.currentVersion
  const pageCount = currentVersion?.document?.pageCount || 0
  const shouldRenderPageSwitcher = share.identifier && pageCount > 1

  return (
    <BreadcrumbWrapper>
      <Breadcrumb>
        {documentName && <Title>{documentName}</Title>}
        {shouldRenderPageSwitcher && (
          <PagesDropdown shareIdentifier={share.identifier} />
        )}
      </Breadcrumb>
    </BreadcrumbWrapper>
  )
}

type EmbeddedDocumentHeaderProps = {
  /**
   * Current document name to render into the header.
   */
  documentName: string
  /**
   * Whether the header should render as logo only, w/o other content.
   */
  empty: boolean
}

/**
 * Allows the section to shrink and render a document name truncated with an
 * ellipsis if required.
 */
const DocumentNameSection = styled(Navbar.Section)`
  flex-basis: auto;
  overflow: hidden;
`

/**
 * Minimal document header component rendered while the app is in an
 * embedded context.
 */
export const EmbeddedDocumentHeader: FC<EmbeddedDocumentHeaderProps> = props => {
  const { documentName, empty } = props

  const origin = window.location.origin
  const pathname = window.location.pathname.replace(/^\/embed/, '')
  const currentUrl = `${origin}${pathname}`

  return (
    <Navbar>
      <DocumentNameSection>
        <ExternalLink
          href={currentUrl}
          title="Open the embed in a new Sketch Cloud tab">
          <Navbar.Logo />
        </ExternalLink>
        {empty === false && (
          <>
            <Navbar.Divider />
            <EmbeddedBreadcrumb documentName={documentName} />
          </>
        )}
      </DocumentNameSection>
    </Navbar>
  )
}
